import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";
import { categoryImages } from "@/mocks/images";

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <section id="categories" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            {t("categories.title")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground-700 md:text-base">
            {t("categories.subtitle")}
          </p>
        </div>

        <div className="mt-8 flex justify-center" ref={ref}>
          <div className="relative w-full max-w-md">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-background-300 bg-background-100 px-4 py-3 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-400 cursor-pointer"
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-filter-3-line text-base text-primary-600"></i>
                </span>
                {t("categories.dropdownLabel")}
              </span>
              <span className="flex h-5 w-5 items-center justify-center">
                <i className={`ri-arrow-down-s-line text-base transition-transform ${open ? "rotate-180" : ""}`}></i>
              </span>
            </button>

            {open && (
              <div className="absolute z-20 mt-2 max-h-80 w-full overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setOpen(false)}
                    className="flex w-full items-center gap-3 whitespace-nowrap px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    {t(`categories.items.${cat.id}`)}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
          {categories.map((cat) => {
            const name = t(`categories.items.${cat.id}`);
            return (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300"
              >
                <div className="h-28 w-full overflow-hidden md:h-32">
                  <img
                    src={categoryImages[cat.id] ?? categoryImages.other}
                    alt={name}
                    title={name}
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center justify-center gap-2 px-2 py-3">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
                    <i className={`${cat.icon} text-base`}></i>
                  </span>
                  <span className="whitespace-nowrap text-sm font-semibold text-foreground-900">{name}</span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}